import { HarmonizedViolationAssigneeSource } from "generated-graphql/graphql";

/**
 * Where a violation's effective assignee came from, or nothing at all when the
 * field didn't resolve.
 */
export type AssigneeSource = HarmonizedViolationAssigneeSource | null | undefined;

/**
 * Shared by the grid cell and the drawer caption so the two can never disagree
 * about which default a row is following.
 */
export function assigneeSourceScopeLabel(source: AssigneeSource): string | null {
  switch (source) {
    case HarmonizedViolationAssigneeSource.Facility:
      return "facility default";
    case HarmonizedViolationAssigneeSource.Region:
      return "region default";
    case HarmonizedViolationAssigneeSource.BusinessUnit:
      return "business unit default";
    default:
      return null;
  }
}

/**
 * The grid cell's short parenthetical — "(region default)" — or null when there
 * is nothing to caption.
 */
export function assigneeInheritanceHint(source: AssigneeSource): string | null {
  const label = assigneeSourceScopeLabel(source);
  return label ? `(${label})` : null;
}

/**
 * Names the default an assignee was inherited from, so a reader can tell it
 * apart from one set on this violation and knows which default to edit.
 */
export function assigneeInheritanceCaption(
  source: AssigneeSource,
  assigneeName: string | null | undefined
): string {
  if (!assigneeName) {
    return "Effective assignee: Unassigned";
  }
  const label = assigneeSourceScopeLabel(source);
  if (!label) {
    return `Effective assignee: ${assigneeName}`;
  }
  return `Inherited from the ${label}: ${assigneeName}`;
}
